import { Layout } from '../components/Layout';
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, ArrowLeftRight, Layers, Star, Pencil, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useActiveLocation } from '../context/LocationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function Inventory({ user, onLogout }) {
  const [stock, setStock] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [areaMap, setAreaMap] = useState({});       // area_id -> name (all locations)
  const [activeAreas, setActiveAreas] = useState([]); // areas for the active location

  const { activeLocation } = useActiveLocation();
  const locationId = activeLocation?.location_id || user?.outlet_id || 'central_kitchen';
  const canAdjust = ['admin', 'manager', 'central_kitchen'].includes(user?.role);

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [form, setForm] = useState({ product_id: '', area_id: '', quantity: '', mode: 'set' });
  const [moveForm, setMoveForm] = useState({ product_id: '', from_area_id: '', to_area_id: '', quantity: '' });
  const [activeStock, setActiveStock] = useState({}); // `${product_id}|${area_id}` -> qty (active location)
  const [newArea, setNewArea] = useState('');
  const [editArea, setEditArea] = useState({ id: null, name: '' });
  const [selected, setSelected] = useState(() => new Set());

  const fetchInventory = async (locationFilter = null) => {
    try {
      const url = locationFilter ? `${API}/inventory/stock?location_id=${locationFilter}` : `${API}/inventory/stock`;
      const res = await axios.get(url, auth());
      setStock(res.data);
    } catch (e) { console.error('inventory', e); }
  };
  const fetchProducts = async () => {
    try { const r = await axios.get(`${API}/products`, auth()); setProducts(r.data); } catch (e) { /* noop */ }
  };
  const loadAreasForLocations = async (locs) => {
    const map = {};
    await Promise.all((locs || []).map(async (l) => {
      try {
        const r = await axios.get(`${API}/storage-areas?location_id=${l.location_id}`, auth());
        (r.data || []).forEach((a) => { map[a.area_id] = a.name; });
        if (l.location_id === locationId) setActiveAreas(r.data || []);
      } catch (e) { /* noop */ }
    }));
    setAreaMap(map);
  };
  const fetchLocations = async () => {
    try {
      const r = await axios.get(`${API}/locations`, auth());
      setLocations(r.data);
      loadAreasForLocations(r.data);
    } catch (e) { /* noop */ }
  };
  const reloadActiveAreas = async () => {
    try {
      const r = await axios.get(`${API}/storage-areas?location_id=${locationId}`, auth());
      setActiveAreas(r.data || []);
      setAreaMap((m) => { const n = { ...m }; (r.data || []).forEach((a) => { n[a.area_id] = a.name; }); return n; });
    } catch (e) { /* noop */ }
  };
  const loadActiveStock = async () => {
    try {
      const r = await axios.get(`${API}/inventory/stock?location_id=${locationId}`, auth());
      const m = {};
      (r.data || []).forEach((s) => { m[`${s.product_id}|${s.area_id || ''}`] = s.quantity; });
      setActiveStock(m);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { fetchInventory(); fetchProducts(); fetchLocations(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { reloadActiveAreas(); loadActiveStock(); /* eslint-disable-next-line */ }, [locationId, adjustOpen, moveOpen, areasOpen]);

  const productName = (id) => products.find((p) => p.product_id === id)?.name || id;
  const locationName = (id) => locations.find((l) => l.location_id === id)?.name || id;
  const sortedProducts = useMemo(() => [...products].sort((a, b) => (a.name || '').localeCompare(b.name || '')), [products]);

  // Group flat stock rows into product+location with per-area breakdown
  const grouped = useMemo(() => {
    const g = {};
    stock.forEach((s) => {
      const key = `${s.product_id}|${s.location_id}`;
      if (!g[key]) g[key] = { product_id: s.product_id, location_id: s.location_id, unit: s.unit, total: 0, areas: {} };
      g[key].total += s.quantity;
      const aid = s.area_id || '__none__';
      g[key].areas[aid] = (g[key].areas[aid] || 0) + s.quantity;
    });
    return Object.values(g).sort((a, b) => productName(a.product_id).localeCompare(productName(b.product_id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock, products]);

  const currentFor = (pid, aid) => activeStock[`${pid}|${aid || ''}`] ?? 0;

  const openAdjust = (presetProduct = '') => {
    const def = activeAreas.find((a) => a.is_default_sale) || activeAreas[0];
    setForm({ product_id: presetProduct || '', area_id: def?.area_id || '', quantity: '', mode: 'set' });
    setAdjustOpen(true);
  };

  const deleteStockRow = async (pid, locId) => {
    if (!window.confirm(`Remove all stock records for "${productName(pid)}" at ${locationName(locId)}? This clears its quantity across all areas at this location.`)) return;
    try {
      await axios.delete(`${API}/inventory/stock?product_id=${encodeURIComponent(pid)}&location_id=${encodeURIComponent(locId)}`, auth());
      toast.success('Stock entry removed');
      fetchInventory(selectedLocation === 'all' ? null : selectedLocation);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  const rowKey = (g) => `${g.product_id}|${g.location_id}`;
  const toggleRow = (key) => setSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const allSelected = grouped.length > 0 && grouped.every((g) => selected.has(rowKey(g)));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(grouped.map(rowKey)));
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} selected stock entr${selected.size === 1 ? 'y' : 'ies'}? This clears their quantities at the shown location(s).`)) return;
    const items = [...selected].map((k) => { const [product_id, location_id] = k.split('|'); return { product_id, location_id }; });
    try {
      const res = await axios.post(`${API}/inventory/stock/bulk-delete`, { items }, auth());
      toast.success(`Removed ${res.data.entries} stock entr${res.data.entries === 1 ? 'y' : 'ies'}`);
      clearSelection();
      fetchInventory(selectedLocation === 'all' ? null : selectedLocation);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };
  const openMove = () => {
    const from = activeAreas.find((a) => !a.is_default_sale) || activeAreas[1] || activeAreas[0];
    const to = activeAreas.find((a) => a.is_default_sale) || activeAreas[0];
    setMoveForm({ product_id: '', from_area_id: from?.area_id || '', to_area_id: to?.area_id || '', quantity: '' });
    setMoveOpen(true);
  };

  const saveAdjust = async () => {
    if (!form.product_id) return toast.error('Select a product');
    if (!form.area_id) return toast.error('Select a storage area');
    if (form.quantity === '' || isNaN(parseFloat(form.quantity))) return toast.error('Enter a quantity');
    try {
      const res = await axios.post(`${API}/inventory/adjust`, {
        product_id: form.product_id, location_id: locationId, area_id: form.area_id,
        quantity: parseFloat(form.quantity), mode: form.mode,
      }, auth());
      toast.success(`Stock updated — ${productName(form.product_id)}: ${res.data.quantity}`);
      setAdjustOpen(false);
      fetchInventory(selectedLocation === 'all' ? null : selectedLocation);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update stock'); }
  };

  const saveMove = async () => {
    if (!moveForm.product_id) return toast.error('Select a product');
    if (!moveForm.from_area_id || !moveForm.to_area_id) return toast.error('Select areas');
    if (moveForm.from_area_id === moveForm.to_area_id) return toast.error('Source and destination must differ');
    if (!parseFloat(moveForm.quantity) || parseFloat(moveForm.quantity) <= 0) return toast.error('Enter a quantity');
    try {
      await axios.post(`${API}/inventory/move`, {
        product_id: moveForm.product_id, location_id: locationId,
        from_area_id: moveForm.from_area_id, to_area_id: moveForm.to_area_id, quantity: parseFloat(moveForm.quantity),
      }, auth());
      toast.success('Stock moved');
      setMoveOpen(false);
      fetchInventory(selectedLocation === 'all' ? null : selectedLocation);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to move stock'); }
  };

  const addArea = async () => {
    if (!newArea.trim()) return;
    try { await axios.post(`${API}/storage-areas`, { location_id: locationId, name: newArea.trim() }, auth()); setNewArea(''); toast.success('Area added'); reloadActiveAreas(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
  };
  const saveAreaName = async (id) => {
    try { await axios.put(`${API}/storage-areas/${id}`, { name: editArea.name.trim() }, auth()); setEditArea({ id: null, name: '' }); toast.success('Updated'); reloadActiveAreas(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const setDefaultArea = async (id) => {
    try { await axios.put(`${API}/storage-areas/${id}`, { is_default_sale: true }, auth()); toast.success('Default sale area set'); reloadActiveAreas(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteArea = async (id) => {
    if (!window.confirm('Delete this storage area?')) return;
    try { await axios.delete(`${API}/storage-areas/${id}`, auth()); toast.success('Deleted'); reloadActiveAreas(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const areaLabel = (aid) => aid === '__none__' ? 'Unassigned' : (areaMap[aid] || 'Area');

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-4xl font-bold text-stone-900">Inventory</h1>
            <p className="text-stone-600 mt-1">Stock by storage area · {activeLocation?.name || locationId}</p>
          </div>
          {canAdjust && (
            <div className="flex gap-2">
              <Button data-testid="manage-areas-button" onClick={() => setAreasOpen(true)} variant="outline" className="border-stone-300">
                <Layers size={18} className="mr-2" /> Manage Areas
              </Button>
              <Button data-testid="move-stock-button" onClick={openMove} variant="outline" className="border-stone-300">
                <ArrowLeftRight size={18} className="mr-2" /> Move Stock
              </Button>
              <Button data-testid="adjust-stock-button" onClick={openAdjust} className="bg-secondary hover:bg-secondary-hover text-black">
                <Plus size={20} className="mr-2" /> Enter / Adjust Stock
              </Button>
            </div>
          )}
        </div>

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm mb-6">
          <div className="p-6 border-b border-stone-200 flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-heading text-2xl font-semibold text-stone-800">Stock Summary</h2>
            <div className="flex items-center gap-3">
              {canAdjust && selected.size > 0 && (
                <div data-testid="bulk-action-bar" className="flex items-center gap-2 bg-functional-error/5 border border-functional-error/30 rounded-sm px-3 py-1.5">
                  <span className="text-sm text-stone-700" data-testid="bulk-selected-count">{selected.size} selected</span>
                  <Button data-testid="bulk-delete-button" onClick={bulkDelete} size="sm" className="bg-functional-error hover:bg-functional-error/90 text-white h-8">
                    <Trash2 size={15} className="mr-1" /> Delete Selected
                  </Button>
                  <button data-testid="bulk-clear-button" onClick={clearSelection} className="text-stone-400 hover:text-stone-700" title="Clear selection"><X size={16} /></button>
                </div>
              )}
              <select
                data-testid="location-filter"
                value={selectedLocation}
                onChange={(e) => { setSelectedLocation(e.target.value); clearSelection(); fetchInventory(e.target.value === 'all' ? null : e.target.value); }}
                className="h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Locations</option>
                {locations.map((loc) => <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>)}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  {canAdjust && (
                    <th className="px-4 py-3 text-left w-10">
                      <input data-testid="select-all-checkbox" type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-primary cursor-pointer" />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">By Storage Area</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-stone-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Status</th>
                  {canAdjust && <th className="px-4 py-3 text-right text-xs font-medium text-stone-500 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {grouped.map((g) => (
                  <tr key={`${g.product_id}|${g.location_id}`} className={`border-b border-stone-100 hover:bg-stone-50 ${selected.has(rowKey(g)) ? 'bg-secondary/5' : ''}`} data-testid={`inv-row-${g.product_id}-${g.location_id}`}>
                    {canAdjust && (
                      <td className="px-4 py-3">
                        <input data-testid={`inv-select-${g.product_id}-${g.location_id}`} type="checkbox" checked={selected.has(rowKey(g))} onChange={() => toggleRow(rowKey(g))} className="h-4 w-4 accent-primary cursor-pointer" />
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium">{productName(g.product_id)}</td>
                    <td className="px-4 py-3 text-stone-600">{locationName(g.location_id)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(g.areas).map(([aid, q]) => (
                          <span key={aid} className={`text-xs font-medium px-2 py-0.5 rounded-full ${aid === '__none__' ? 'bg-stone-100 text-stone-500' : 'bg-secondary/20 text-stone-800'}`}>
                            {areaLabel(aid)}: <span className="font-mono">{q}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold" data-testid={`inv-total-${g.product_id}-${g.location_id}`}>{g.total.toFixed(2)} <span className="text-stone-400 text-xs">{g.unit}</span></td>
                    <td className="px-4 py-3">
                      {g.total < 10 ? (
                        <span className="px-2 py-1 bg-functional-error/10 text-functional-error rounded-sm text-xs font-medium">Low Stock</span>
                      ) : (
                        <span className="px-2 py-1 bg-functional-success/10 text-functional-success rounded-sm text-xs font-medium">In Stock</span>
                      )}
                    </td>
                    {canAdjust && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button data-testid={`inv-edit-${g.product_id}-${g.location_id}`} onClick={() => openAdjust(g.product_id)} className="text-stone-500 hover:text-primary mr-3" title="Edit stock"><Pencil size={16} /></button>
                        <button data-testid={`inv-delete-${g.product_id}-${g.location_id}`} onClick={() => deleteStockRow(g.product_id, g.location_id)} className="text-stone-500 hover:text-functional-error" title="Remove stock entry"><Trash2 size={16} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {grouped.length === 0 && <div className="text-center py-12 text-stone-400">No stock items found</div>}
          </div>
        </div>
      </div>

      {/* Adjust Stock */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent data-testid="adjust-stock-dialog">
          <DialogHeader><DialogTitle>Enter / Adjust Stock</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-stone-600">Location: <span className="font-semibold text-stone-900" data-testid="adjust-location-label">{activeLocation?.name || locationId}</span></div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Product</label>
              <Select value={form.product_id || undefined} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="adjust-product-select"><SelectValue placeholder="Select a product" /></SelectTrigger>
                <SelectContent className="max-h-72">{sortedProducts.filter((p) => p.product_id).map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Storage Area</label>
                <Select value={form.area_id || undefined} onValueChange={(v) => setForm({ ...form, area_id: v })}>
                  <SelectTrigger data-testid="adjust-area-select"><SelectValue placeholder="Select area" /></SelectTrigger>
                  <SelectContent>{activeAreas.map((a) => <SelectItem key={a.area_id} value={a.area_id}>{a.name}{a.is_default_sale ? ' (sells)' : ''}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Mode</label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger data-testid="adjust-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="set">Set to (exact)</SelectItem><SelectItem value="add">Add to current</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {form.product_id && form.area_id && (
              <p className="text-xs text-stone-500" data-testid="adjust-current-stock">Current in this area: <span className="font-semibold">{currentFor(form.product_id, form.area_id)}</span></p>
            )}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{form.mode === 'add' ? 'Quantity to add' : 'New quantity'}</label>
              <Input data-testid="adjust-qty-input" type="number" placeholder="e.g. 20" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
          </div>
          <Button data-testid="adjust-save-button" onClick={saveAdjust} className="w-full bg-primary text-white">Save</Button>
        </DialogContent>
      </Dialog>

      {/* Move Stock */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent data-testid="move-stock-dialog">
          <DialogHeader><DialogTitle>Move Stock Between Areas</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-stone-600">Location: <span className="font-semibold text-stone-900">{activeLocation?.name || locationId}</span></div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Product</label>
              <Select value={moveForm.product_id || undefined} onValueChange={(v) => setMoveForm({ ...moveForm, product_id: v })}>
                <SelectTrigger data-testid="move-product-select"><SelectValue placeholder="Select a product" /></SelectTrigger>
                <SelectContent className="max-h-72">{sortedProducts.filter((p) => p.product_id).map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">From</label>
                <Select value={moveForm.from_area_id || undefined} onValueChange={(v) => setMoveForm({ ...moveForm, from_area_id: v })}>
                  <SelectTrigger data-testid="move-from-select"><SelectValue placeholder="From area" /></SelectTrigger>
                  <SelectContent>{activeAreas.map((a) => <SelectItem key={a.area_id} value={a.area_id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">To</label>
                <Select value={moveForm.to_area_id || undefined} onValueChange={(v) => setMoveForm({ ...moveForm, to_area_id: v })}>
                  <SelectTrigger data-testid="move-to-select"><SelectValue placeholder="To area" /></SelectTrigger>
                  <SelectContent>{activeAreas.map((a) => <SelectItem key={a.area_id} value={a.area_id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {moveForm.product_id && moveForm.from_area_id && (
              <p className="text-xs text-stone-500">Available in source: <span className="font-semibold">{currentFor(moveForm.product_id, moveForm.from_area_id)}</span></p>
            )}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Quantity to move</label>
              <Input data-testid="move-qty-input" type="number" placeholder="e.g. 5" value={moveForm.quantity} onChange={(e) => setMoveForm({ ...moveForm, quantity: e.target.value })} />
            </div>
          </div>
          <Button data-testid="move-save-button" onClick={saveMove} className="w-full bg-primary text-white">Move</Button>
        </DialogContent>
      </Dialog>

      {/* Manage Areas */}
      <Dialog open={areasOpen} onOpenChange={setAreasOpen}>
        <DialogContent data-testid="manage-areas-dialog">
          <DialogHeader><DialogTitle>Storage Areas · {activeLocation?.name || locationId}</DialogTitle></DialogHeader>
          <div className="flex gap-2 py-2">
            <Input data-testid="new-area-input" placeholder="New area (e.g. Freezer)" value={newArea} onChange={(e) => setNewArea(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addArea(); }} />
            <Button data-testid="add-area-button" onClick={addArea} className="bg-primary text-white shrink-0"><Plus size={18} className="mr-1" /> Add</Button>
          </div>
          <p className="text-xs text-stone-500 -mt-1 mb-2">The area marked with a star is where POS sales are deducted from.</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {activeAreas.map((a) => (
              <div key={a.area_id} data-testid={`area-row-${a.area_id}`} className="flex items-center gap-2 border border-stone-200 rounded-sm px-3 py-2">
                {editArea.id === a.area_id ? (
                  <>
                    <Input value={editArea.name} onChange={(e) => setEditArea({ ...editArea, name: e.target.value })} className="h-8" />
                    <Button size="sm" onClick={() => saveAreaName(a.area_id)} className="bg-primary text-white">Save</Button>
                    <button onClick={() => setEditArea({ id: null, name: '' })} className="text-stone-400 text-sm px-1">Cancel</button>
                  </>
                ) : (
                  <>
                    <button title="Set as sale area" data-testid={`area-default-${a.area_id}`} onClick={() => setDefaultArea(a.area_id)} className={a.is_default_sale ? 'text-secondary' : 'text-stone-300 hover:text-secondary'}>
                      <Star size={16} fill={a.is_default_sale ? 'currentColor' : 'none'} />
                    </button>
                    <span className="flex-1 text-sm text-stone-800">{a.name}{a.is_default_sale && <span className="ml-2 text-xs text-stone-400">(sells from here)</span>}</span>
                    <button onClick={() => setEditArea({ id: a.area_id, name: a.name })} className="text-stone-400 hover:text-primary"><Pencil size={15} /></button>
                    <button data-testid={`area-del-${a.area_id}`} onClick={() => deleteArea(a.area_id)} className="text-stone-400 hover:text-functional-error"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            ))}
            {activeAreas.length === 0 && <p className="text-center text-stone-400 py-6 text-sm">No areas yet</p>}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
